import { PhotoTransforms } from '~/common/photos/transforms'
import { CampsiteDetails } from '../types'

interface Photo {
  caption: string
  url: PhotoTransforms
}

export function getPrimaryPhoto(
  campsite: CampsiteDetails,
  categoryIds?: string[],
): Photo {
  if (!campsite.primaryPhoto) {
    return emptyPhoto
  }
  if (categoryIds && categoryIds.length > 0 && campsite.primaryPhotos) {
    const photo = getPrimaryPhotoForCategory(
      campsite.primaryPhotos,
      categoryIds,
    )
    if (photo) {
      return photo
    }
  }
  return campsite.primaryPhoto
}

const emptyPhoto = {
  url: {
    masterImage: '',
  },
  caption: '',
}

function getPrimaryPhotoForCategory(
  photos: Record<string, Photo>,
  categoryIds: string[],
): Photo | undefined {
  for (const categoryId of categoryIds) {
    if (photos[categoryId]) {
      return photos[categoryId]
    }
  }
  return undefined
}

/**
 * Gets the primary photo from the most expensive pitch type matching the given category IDs.
 *
 * @param campsite - The campsite details object
 * @param categoryIds - Array of category IDs (as strings) to match against
 * @returns The primary photo for the matching pitch type, or null if not found
 *
 * @example
 * const campsite = {
 *   pitchtypes: [
 *     {
 *       leadPrice: { amount: 50 },
 *       categories: [{ id: '1' }],
 *       photos: [{ caption: 'Tent', url: { masterImage: 'tent.jpg' } }]
 *     },
 *     {
 *       leadPrice: { amount: 100 },
 *       categories: [{ id: '2' }],
 *       photos: [{ caption: 'Caravan', url: { masterImage: 'caravan.jpg' } }]
 *     }
 *   ],
 *   primaryPhoto: { caption: 'Default', url: { masterImage: 'default.jpg' } }
 * };
 *
 * const photo = getPrimaryPhotoFromPitchType(campsite, ['2']);
 * console.log(photo); // Outputs: { caption: 'Caravan', url: { masterImage: 'caravan.jpg' } }
 */
export function getPrimaryPhotoFromPitchType(
  campsite: CampsiteDetails,
  categoryIds: string[],
): Photo | null {
  categoryIds = categoryIds ?? []
  const sortedPitchTypes = campsite.pitchtypes
    .slice()
    .sort((a, b) => (b.leadPrice?.amount ?? 0) - (a.leadPrice?.amount ?? 0))
  const matchingPitchType = sortedPitchTypes.find(
    (pitchType) =>
      pitchType.categories.some((category) =>
        categoryIds.includes(category.id),
      ) && pitchType.photos?.[0],
  )
  return matchingPitchType?.photos?.[0] ?? campsite.primaryPhoto ?? null
}
